import { FC, HTMLAttributes, useMemo, useState, useEffect } from "react";
import { useQuery } from "@apollo/client/react/hooks/useQuery";
import {
  meGQL,
  Query,
  getUserProfileCaGQL,
  featuredCardsGql,
  getUserNotificationsGQL,
} from "@onmo/graphql/graphql";
import { Console as ConsoleLoading } from "../../components/common/PageLoading/Console";
import { useStyles } from "./Console.style";
import { useAppSelector } from "../../components/hooks";
import { selectIsAuthenticated } from "@onmo/auth";
import ConsoleMobileContent from "../../components/common/ConsoleMobileContent/ConsoleMobileContent";
import ConsoleDesktopContent from "../../components/common/ConsoleDesktopContent/ConsoleDesktopContent";
import { selectIsDesktopView } from "../../store/slices/appState";
import {
  FeatureDesktopGroup,
  FeatureMobileGroup,
} from "../../constants/console";
import { WelcomePopup } from "../../components/common/WelcomePopup";
import { isFounderNotifications } from "../../models/user";

const Console: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const classes = useStyles();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isDesktop = useAppSelector(selectIsDesktopView);
  const { data: dataMe, loading: loadingMe } = useQuery<{
    me: Query["me"];
  }>(meGQL, {
    skip: !isAuthenticated,
  });
  const { data: dataProfile } = useQuery<{ user: Query["user"] }>(
    getUserProfileCaGQL,
    {
      skip: !dataMe?.me?.id,
      variables: { id: dataMe?.me?.id },
    },
  );
  const { loading: loadingFeaturedCards } = useQuery<Query>(featuredCardsGql, {
    variables: {
      input: { group: isDesktop ? FeatureDesktopGroup : FeatureMobileGroup },
    },
  });
  const { data: dataNotifications, refetch } = useQuery(
    getUserNotificationsGQL,
    {
      variables: { product: "aaa", userId: dataMe?.me?.id },
      skip: !dataMe?.me?.id,
      fetchPolicy: "cache-and-network",
    },
  );
  const { isBonus, isFounder } = isFounderNotifications(
    dataNotifications?.user?.notifications || [],
  );
  const [isOpenWelcome, setIsOpenWelcome] = useState(!!isBonus || !!isFounder);
  const loading = loadingMe || loadingFeaturedCards;

  const getTimePlayed = useMemo(() => {
    if (!dataProfile?.user?.totalTimePlayed) return 0;
    const hour = dataProfile?.user?.totalTimePlayed / 3600;
    if (hour <= 1) {
      return Math.floor(hour * 10) / 10;
    }
    return Math.ceil(hour - 0.5);
  }, [dataProfile?.user?.totalTimePlayed]);

  const onCloseWelcome = async () => {
    setIsOpenWelcome(false);
    await refetch();
  };

  useEffect(() => {
    setIsOpenWelcome(!!isBonus || !!isFounder);
  }, [isBonus, isFounder]);

  return (
    <div className={classes.root}>
      {loading ? (
        <ConsoleLoading />
      ) : isDesktop ? (
        <ConsoleDesktopContent
          totalTimePlayed={getTimePlayed}
          totalPlayedGames={dataProfile?.user?.totalPlayedGames || 0}
        />
      ) : (
        <ConsoleMobileContent
          user={dataMe?.me}
          totalTimePlayed={getTimePlayed}
          totalPlayedGames={dataProfile?.user?.totalPlayedGames || 0}
        />
      )}
      <WelcomePopup isOpen={isOpenWelcome} onClose={onCloseWelcome} />
    </div>
  );
};

export default Console;
